import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { shallowEqual } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import {
    SanctionEvent,
    SanctionEventDetail,
    SanctionEventEntity,
    SanctionEventMetadata,
    SanctionEventType,
} from "../../../api/sanctionEvents";
import { CreditFacility } from "../../../api/credit_accounts";
import {
    getCreditAccountEntities,
    getCreditAccountsByFacilityCategory,
} from "../../creditAccounts/logic/slice";
import {
    CREDIT_FACILITY_SHORT_LABELS,
    FacilityCategory,
} from "../../creditAccounts/logic/creditAccountMappings";
import {
    selectSanctionEventsError,
    selectSanctionEventsSortedByDate,
    selectSanctionEventsStatus,
} from "./sanctionEventsSlice";
import {
    fetchSanctionEvents,
    upsertSanctionEvents,
} from "./sanctionEventsThunk";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import { OptionNameValuePair } from "../../../lib/interfaces/types";

export interface SanctionEventRow extends SanctionEvent {
    isAdded: boolean;
    isDirty: boolean;
}

export interface SanctionEventsTableVM {
    rows: SanctionEventRow[];
    isLoading: boolean;
    isSaving: boolean;
    error?: string | null;
    hasChanges: boolean;
    hasValidationErrors: boolean;
    addRow: () => void;
    deleteRow: (rowIndex: number) => void;
    updateRow: (
      rowIndex: number,
      value: unknown,
      field: keyof SanctionEventRow,
    ) => void;
    updateDetails: (
      rowIndex: number,
      details: SanctionEventDetail[],
    ) => void;
    getCreditFacilityForLoanAccount: (
      loanAccountNumber: string,
    ) => CreditFacility;
    getLoanAccountOptions: (
      category: FacilityCategory,
    ) => OptionNameValuePair<string>[];
    retry: () => void;
    save: () => Promise<void>;
    discard: () => void;
}

export const useSanctionEventsTableVM = (): SanctionEventsTableVM => {
    const { id: caseId } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();

    const canonicalRows = useAppSelector(selectSanctionEventsSortedByDate, shallowEqual);
    const status = useAppSelector(selectSanctionEventsStatus);
    const error = useAppSelector(selectSanctionEventsError);
    const creditAccountEntities = useAppSelector(getCreditAccountEntities, shallowEqual);
    const termLoanCreditAccounts = useAppSelector(
      (state) =>
        getCreditAccountsByFacilityCategory(state, FacilityCategory.TERM_LOAN),
      shallowEqual,
    );
    const workingCapitalCreditAccounts = useAppSelector(
      (state) =>
        getCreditAccountsByFacilityCategory(
          state,
          FacilityCategory.WORKING_CAPITAL,
        ),
      shallowEqual,
    );
    const nonFundBasedCreditAccounts = useAppSelector(
      (state) =>
        getCreditAccountsByFacilityCategory(
          state,
          FacilityCategory.NON_FUND_BASED,
        ),
      shallowEqual,
    );

    const [localData, setLocalData] = useState<SanctionEventRow[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      setLocalData(
        canonicalRows.map((row) => ({
          ...row,
          isAdded: false,
          isDirty: false,
        })),
      );
    }, [canonicalRows]);

    const hasChanges = useMemo(
      () => localData.some((row) => row.isDirty),
      [localData],
    );

    const hasValidationErrors = useMemo(
      () =>
        localData
          .filter((row) => row.isDirty)
          .some((row) => {
            if (!row.authority_name) return true;
            if (!row.event_type) return true;
            if (!row.sanction_date) return true;

            return row.details.some((detail) => {
              if (!detail.detail_id) return true;
              if (!detail.loan_account_number) return true;
              if (detail.sanction_amount === null || detail.sanction_amount === undefined) {
                return true;
              }

              return Number.isNaN(Number(detail.sanction_amount));
            });
          }),
      [localData],
    );

    const retry = () => {
      if (caseId) {
        dispatch(fetchSanctionEvents(caseId));
      }
    };

    const addRow = () => {
      const newRow: SanctionEventRow = {
        event_id: uuidv4(),
        authority_name: "",
        event_type: undefined as unknown as SanctionEventType,
        sanction_date: "",
        details: [],
        isAdded: true,
        isDirty: true,
      };

      setLocalData((prev) => [...prev, newRow]);
    };

    const deleteRow = (rowIndex: number) => {
      setLocalData((prev) => {
        const row = prev[rowIndex];
        if (!row?.isAdded) return prev;

        return prev.filter((_, idx) => idx !== rowIndex);
      });
    };

    const updateRow = (
      rowIndex: number,
      value: unknown,
      field: keyof SanctionEventRow,
    ) => {
      setLocalData((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex ? { ...row, [field]: value, isDirty: true } : row,
        ),
      );
    };

    const updateDetails = (
      rowIndex: number,
      details: SanctionEventDetail[],
    ) => {
      setLocalData((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex ? { ...row, details, isDirty: true } : row,
        ),
      );
    };

    // This is the correct implementation, see note below.
    const getCreditFacilityForLoanAccount = useCallback(
      (loanAccountNumber: string): CreditFacility =>
        creditAccountEntities[loanAccountNumber].credit_facility,
      [creditAccountEntities],
    );

    // 11 Aug, 2026, Soumanta Das
    // IMP NOTE & TODO - The correct implementation for `getCreditFacilityForLoanAccount` is 
    // present above. However, we have some bad data in our staging DB for which no credit facility
    // is present even after the data migration and hence the code above causes runtime failures,
    // thereby causing issues in development & testing.
    // E.g. - LN0012345678, LN0012345679 (fixed now by Nirmay, but can't guarantee for the Loan Accts)
    // const getCreditFacilityForLoanAccount = useCallback(
    //   (loanAccountNumber: string): CreditFacility => {
    //     const facility = creditAccountEntities[loanAccountNumber]?.credit_facility;
    //     if (!facility) {
    //       console.warn(`Missing credit facility for account: ${loanAccountNumber}`);
    //     }
    //     return facility;
    //   },
    //   [creditAccountEntities],
    // );

    const creditAccountsByCategory = useMemo(
      () => ({
        [FacilityCategory.TERM_LOAN]: termLoanCreditAccounts,
        [FacilityCategory.WORKING_CAPITAL]: workingCapitalCreditAccounts,
        [FacilityCategory.NON_FUND_BASED]: nonFundBasedCreditAccounts,
        [FacilityCategory.OTHER]: [],
      }),
      [
        termLoanCreditAccounts,
        workingCapitalCreditAccounts,
        nonFundBasedCreditAccounts,
      ],
    );

    const getLoanAccountOptions = useCallback(
      (category: FacilityCategory): OptionNameValuePair<string>[] =>
        creditAccountsByCategory[category].map((account) => ({
          value: account.account_number,
          name: `${account.ext_account_number} - ${CREDIT_FACILITY_SHORT_LABELS[account.credit_facility]}`,
        })),
      [creditAccountsByCategory],
    );

    const save = async () => {
      if (!caseId) return;

      const dirtyRows = localData.filter((row) => row.isDirty);
      if (dirtyRows.length === 0 || hasValidationErrors) return;

      try {
        setIsSaving(true);

        const eventsMetadataToUpsert: SanctionEventMetadata[] = dirtyRows.map(
          ({ details, isDirty, isAdded, ...metadata }) => metadata,
        );

        const eventsDetailsToUpsert: SanctionEventEntity[] = dirtyRows.flatMap(
          (row) =>
            row.details.map((detail) => ({
              ...detail,
              event_id: row.event_id,
            })),
        );

        await dispatch(
          upsertSanctionEvents({
            case_id: caseId,
            request: {
              events_metadata: eventsMetadataToUpsert,
              events_details: eventsDetailsToUpsert,
            },
          }),
        ).unwrap();

        showSuccessToast("Sanction events saved successfully");
      } catch {
        showErrorToast("Failed to save sanction events. Please try again.");
      } finally {
        setIsSaving(false);
      }
    };

    const discard = () => {
      setLocalData(
        canonicalRows.map((row) => ({
          ...row,
          isAdded: false,
          isDirty: false,
        })),
      );
    };

    return {
      rows: localData,
      isLoading: status === "loading" && !isSaving,
      isSaving,
      error,
      hasChanges,
      hasValidationErrors,
      addRow,
      deleteRow,
      updateRow,
      updateDetails,
      getCreditFacilityForLoanAccount,
      getLoanAccountOptions,
      retry,
      save,
      discard,
    };
};
